import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import CloseIconButton from './CloseIconButton'
import { markGalleryClosed, armPhantomClickGuard } from './galleryCooldown'

export default function GalleryModal({ photos = [], id, onClose }) {
  // Click-triggered closes mark the reopen cooldown and arm the phantom-click guard; Escape does
  // neither, since it produces no phantom click underneath. See src/components/README.md.
  const handleClose = () => {
    markGalleryClosed(id)
    armPhantomClickGuard()
    onClose()
  }

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = "";
    }
  }, [])

  // Kept separate from the mount-only effect above, keyed on onClose, so a future
  // call site passing a non-stable handler still binds the current one (exhaustive-deps).
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose])

  const isMac = navigator.userAgent.includes('Mac');
  const containerStyle = {
    paddingRight: isMac ? '24px' : '7px',
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center"
      style={{ background: 'rgba(0,0,0,0.55)', padding: '40px 60px' }}
      onClick={handleClose}
    >
      {/* Non-scrolling wrapper so the close button stays pinned above the grid
          instead of scrolling away with it on a long photo set. */}
      <div
        className="relative w-full"
        style={{ maxWidth: 1300 }}
        onClick={e => e.stopPropagation()}
      >
        {/* No Figma frame for this state — text-brand-muted alone is ~1.3:1 over a photo. Scrim
            classes sit on the button itself, not a wrapping div: a wrapper here previously left its
            outer ring hover-highlighted but unclickable. ConfirmModal stays bare — it passes no
            appearance override, so it keeps CloseIconButton's own default. */}
        <CloseIconButton
          onClick={handleClose}
          ariaLabel="Close gallery"
          appearance="w-8 h-8 rounded-full bg-black/70 hover:bg-white/80 text-brand-muted hover:text-brand-dark"
          className="absolute top-[16px] right-[16px] z-10"
        />

        <div
          className="bg-white rounded-2xl w-full overflow-y-auto scrollbar-gutter-stable"
          style={{ maxHeight: 'calc(100vh - 80px)', overscrollBehavior:'contain' }}
        >
          <div className="p-[24px] grid grid-cols-3 gap-[8px]" style={containerStyle}>
            {photos.map((photo, i) => (
              <div
                key={i}
                className="rounded-[8px] overflow-hidden bg-brand-grayGreen"
                style={{ aspectRatio: '3/2' }}
              >
                <img
                  src={photo.href}
                  alt={`View ${i + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.getElementById('portal')
  )
}
